import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, MenuItem, Select, FormControl,
  InputLabel, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LayersIcon from '@mui/icons-material/Layers';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const PURPLE = '#6d28d9';
const GREEN  = '#166534';
const GREEN_DK = '#1b4332';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.72rem', textTransform: 'uppercase',
  backgroundColor: PURPLE,
  borderRight: '1px solid rgba(255,255,255,0.15)', py: 1.0, px: 0.8,
  whiteSpace: 'nowrap', textAlign: 'center',
};
const CELL_SX = { py: 0.8, px: 0.8, fontSize: '0.75rem', textAlign: 'center' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function fmtTime(val) {
  if (!val) return '—';
  const s = String(val);
  if (s.includes('T') || s.includes(' ')) {
    const parts = s.split(/T| /);
    return parts[1] ? parts[1].substring(0, 5) : s;
  }
  return s.substring(0, 5);
}

function fmtDateShort(val) {
  if (!val) return '—';
  const s = String(val).substring(0, 10);
  const [yr, mo, dy] = s.split('-');
  return `${dy}/${mo}/${yr ? yr.substring(2) : ''}`;
}

function isGrandTotal(row) {
  return String(row.PRESSNO || row.COMPANY || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

// ─── Detail table ─────────────────────────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 580, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 2300 }}>
        <TableHead>
          <TableRow>
            {[
              'SR', 'LOT NUMBER', 'THICK', 'SIZE', 'QUALITY', 'CERT',
              'GLUE', 'FACE', 'PRESS NO.', 'COMPANY', 'PRESSURE', 'TEMP.',
              'LOAD TIME', 'UNLOAD TIME', 'CURING',
              'NO OF GLUE LINE', 'NO OF DRY LINE', 'NO OF FACE LINE', 'FACE SQ.M',
              'REFACE DATE', 'SHIFT',
              'PCS', 'CBM', 'REJ. PCS', 'REJ. CBM', 'TOT. PCS', 'TOT. CBM',
            ].map(h => <TableCell key={h} sx={HDR_SX}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const sshift = String(row.REFACE_SHIFT || '').toUpperCase();
            const shiftColor = sshift === 'NIGHT' ? '#1565c0' : GREEN;
            const sizeset = row.SIZESET ? String(row.SIZESET).replace(/_/g, ' × ') : '—';
            return (
              <TableRow key={i} sx={{
                backgroundColor: i % 2 === 0 ? '#fff' : '#faf5ff',
                '&:hover': { backgroundColor: '#ede9fe' },
              }}>
                <TableCell sx={CELL_SX}>{row.row_num || i + 1}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: PURPLE }}>{row.LOT_NUMBER || '—'}</TableCell>
                <TableCell sx={CELL_SX}>{fmtNum(row.THICKNESS, 2)}</TableCell>
                <TableCell sx={CELL_SX}>{sizeset}</TableCell>
                <TableCell sx={CELL_SX}>{row.QUALITY || '—'}</TableCell>
                <TableCell sx={CELL_SX}>{row.CERTIFICATE || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.GLUETYPE || '—'}</TableCell>
                <TableCell sx={CELL_SX}>{row.FACENAME || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: PURPLE }}>{row.REFACE_PRESSNO || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{row.COMPANY || '—'}</TableCell>
                <TableCell sx={CELL_SX}>{fmtNum(row.PRESURE, 1)}</TableCell>
                <TableCell sx={CELL_SX}>{fmtNum(row.TEMPERATURE, 1)}</TableCell>
                <TableCell sx={CELL_SX}>{fmtTime(row.LOADTIME)}</TableCell>
                <TableCell sx={CELL_SX}>{fmtTime(row.UNLOADTIME)}</TableCell>
                <TableCell sx={CELL_SX}>{fmtNum(row.CURINGTIME, 2)}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: '#1565c0' }}>{fmtNum(row.NO_OF_GLUELINE, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: '#1565c0' }}>{fmtNum(row.NO_OF_DRYLINE, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: '#1565c0' }}>{fmtNum(row.NO_OF_FACE_LINE, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: '#7c3aed' }}>{fmtNum(row.CBM_FACE ?? row.FACE_LINE_SQ_MTRS, 3)}</TableCell>
                <TableCell sx={CELL_SX}>{fmtDateShort(row.REFACE_DATE)}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: shiftColor }}>{sshift || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: GREEN_DK }}>{fmtNum(row.CBM, 3)}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: '#dc2626' }}>{fmtNum(row.REJECTPCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, color: '#dc2626' }}>{fmtNum(row.REJECTCBM, 3)}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#1e3a5f' }}>{fmtNum(row.TOTALPCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: 600, color: '#1e3a5f' }}>{fmtNum(row.TOTALCBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Summary table ────────────────────────────────────────────────────────────
function SummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            {['PRESS NO.', 'COMPANY', 'PCS', 'CBM'].map(h => (
              <TableCell key={h} sx={HDR_SX}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#faf5ff'),
                '&:hover': { filter: 'brightness(0.97)' },
              }}>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : PURPLE }}>
                  {row.PRESSNO || '—'}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 500, color: grand ? '#92400e' : '#374151' }}>
                  {grand ? '—' : (row.COMPANY || '—')}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN }}>
                  {fmtNum(row.TOTALPCS, 0)}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN_DK }}>
                  {fmtNum(row.TOTALCBM, 3)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TAB_LABELS = ['DETAIL', 'SUMMARY'];

// ─── Main view ────────────────────────────────────────────────────────────────
const RefaceTransReportView = ({ onBackClick, user }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate]     = useState(null);
  const [shift, setShift]       = useState('BOTH');
  const [machine, setMachine]   = useState('ALL');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [tab, setTab]           = useState(0);

  // Load machine list on mount (use last 30 days as default date range to get RS[2])
  React.useEffect(() => {
    const loadMachines = async () => {
      try {
        const today = new Date();
        const past  = new Date();
        past.setDate(past.getDate() - 30);
        const toYMD   = today.toISOString().split('T')[0];
        const fromYMD = past.toISOString().split('T')[0];
        const res = await fetch(`${API_URL}/order/refacetransreport`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from_date: fromYMD, to_date: toYMD, shift: null, machine: null }),
        });
        const apiData = await res.json();
        if (apiData.data?.[2]?.length) setMachines(apiData.data[2]);
      } catch (_) {}
    };
    loadMachines();
  }, []);

  const fetchData = async () => {
    if (!fromDate || !toDate) { setError('Please select both From and To dates'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/refacetransreport`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
          shift:     shift === 'BOTH' ? null : shift,
          machine:   machine === 'ALL' ? null : machine,
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || (apiData.data[0]?.length === 0 && apiData.data[1]?.length === 0)) {
        setError('No data found for the selected filters.');
        return;
      }
      // If machines dropdown is empty but we got RS[2], hydrate it
      if (machines.length === 0 && apiData.data[2]?.length) setMachines(apiData.data[2]);
      setData(apiData.data);
      setTab(0);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    if (data[0]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[0]), 'Detail');
    if (data[1]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[1]), 'Summary');
    if (data[2]?.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[2]), 'Machines');
    XLSX.writeFile(wb, `RefaceTrans_${shift}_${machine}_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Header card */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(109,40,217,0.1)', border: '1px solid #ede9fe' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: PURPLE }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${PURPLE} 0%, #7c3aed 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <LayersIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: PURPLE }}>Hotpress Reface Trans Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Reface press cycles — summary by machine, date range and shift</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="BOTH">BOTH (DAY + NIGHT)</MenuItem>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Machine</InputLabel>
                <Select value={machine} label="Machine" onChange={e => setMachine(e.target.value)}>
                  <MenuItem value="ALL">ALL MACHINES</MenuItem>
                  {machines.map(m => (
                    <MenuItem key={m.PRESSNO} value={m.PRESSNO}>
                      {m.PRESSNO}{m.COMPANY ? ` (${m.COMPANY})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={fetchData}
                disabled={loading}
                sx={{
                  minWidth: 140,
                  backgroundColor: PURPLE,
                  '&:hover': { backgroundColor: '#5b21b6' },
                  borderRadius: 2, textTransform: 'none', fontWeight: 600,
                }}
              >
                {loading ? 'Loading…' : 'Search'}
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: PURPLE }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #ede9fe' }}>
          <Box sx={{
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            px: 2, pt: 1,
          }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' },
                '& .Mui-selected': { color: PURPLE },
                '& .MuiTabs-indicator': { backgroundColor: PURPLE },
              }}
            >
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: PURPLE, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}>
              Excel
            </Button>
          </Box>

          {tab === 0 && <DetailTable  rows={data[0]} />}
          {tab === 1 && <SummaryTable rows={data[1]} />}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label="Send Reface Trans Report to WhatsApp"
            apiUrl={fromDate && toDate ? `${API_URL}/api/reface-trans/send` : null}
            payload={{
              from_date: fromDate?.format('YYYY-MM-DD'),
              to_date:   toDate?.format('YYYY-MM-DD'),
              shift,
              machine,
            }}
            disabled={!fromDate || !toDate}
          />
        </Box>
      )}
    </Container>
  );
};

export default RefaceTransReportView;
